import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const BLOG_CATEGORIES = [
  { value: 'fabric_care',      label: 'Fabric Care' },
  { value: 'design_trends',    label: 'Design Trends' },
  { value: 'wholesale_guide',  label: 'Wholesale Guide' },
  { value: 'production_tips',  label: 'Production Tips' },
  { value: 'color_dye',        label: 'Color & Dye' },
  { value: 'sustainability',   label: 'Sustainability' },
];

const PRODUCT_CATEGORIES = [
  { value: 'all',              label: 'All Fabrics' },
  { value: 'digital_printed',  label: 'Digital Printed' },
  { value: 'plain_dyed',       label: 'Plain Dyed' },
];

const STATUS_COLORS = {
  // Topics
  suggested:   '#f59e0b',
  approved:    '#3b82f6',
  rejected:    '#ef4444',
  generating:  '#8b5cf6',
  generated:   '#10b981',
  failed:      '#dc2626',
  // Posts
  draft:          '#f59e0b',
  publishing:     '#8b5cf6',
  published:      '#10b981',
  publish_failed: '#dc2626',
};

function StatusBadge({ status }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 600,
      backgroundColor: (STATUS_COLORS[status] || '#6b7280') + '22',
      color: STATUS_COLORS[status] || '#6b7280',
      border: `1px solid ${STATUS_COLORS[status] || '#6b7280'}44`,
      textTransform: 'capitalize',
    }}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
}

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function BlogPage() {
  const { currentUser, canAccessAction } = useAuth();
  const [activeTab, setActiveTab] = useState('topics');

  // ── Dashboard state ──────────────────────────────────────────────────────────
  const [dashLoading, setDashLoading]   = useState(false);
  const [dashData, setDashData]         = useState(null);
  const [dashError, setDashError]       = useState('');

  // ── Topics state ─────────────────────────────────────────────────────────────
  const [topics, setTopics]             = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [topicsError, setTopicsError]   = useState('');
  const [topicStatusFilter, setTopicStatusFilter] = useState('');
  const [showAddForm, setShowAddForm]   = useState(false);
  const [addForm, setAddForm]           = useState({
    title: '', description: '', blog_category: 'design_trends',
    product_category: 'all', target_keywords: '', priority: 3, user_notes: '',
  });
  const [addLoading, setAddLoading]     = useState(false);
  const [addError, setAddError]         = useState('');

  // ── Posts state ──────────────────────────────────────────────────────────────
  const [posts, setPosts]               = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsError, setPostsError]     = useState('');
  const [postStatusFilter, setPostStatusFilter] = useState('');
  const [previewPostId, setPreviewPostId] = useState(null);
  const [previewPost, setPreviewPost]   = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // ── Suggestion state ─────────────────────────────────────────────────────────
  const [suggestLoading, setSuggestLoading] = useState(false);

  // ── Generation loading state ─────────────────────────────────────────────────
  const [generatingTopicId, setGeneratingTopicId]   = useState(null);
  const [regeneratingPostId, setRegeneratingPostId] = useState(null);

  // ── Topic inline editing ──────────────────────────────────────────────────────
  const [editingTopicId, setEditingTopicId]         = useState(null);
  const [editingTopicTitle, setEditingTopicTitle]   = useState('');
  const [editingKwTopicId, setEditingKwTopicId]     = useState(null);
  const [editingKwValue, setEditingKwValue]         = useState('');

  // ── Collection picker modal ───────────────────────────────────────────────────
  const [collectionModalTopic, setCollectionModalTopic] = useState(null); // topic obj
  const [allCollections, setAllCollections]             = useState([]);
  const [collectionSearch, setCollectionSearch]         = useState('');
  const [savingCollections, setSavingCollections]       = useState(false);

  // ── AI Monitor state ─────────────────────────────────────────────────────────
  const [monitorQueries, setMonitorQueries]       = useState([]);
  const [monitorResults, setMonitorResults]       = useState([]);
  const [monitorSummary, setMonitorSummary]       = useState(null);
  const [monitorLoading, setMonitorLoading]       = useState(false);
  const [addQueryForm, setAddQueryForm]           = useState({ query: '', category: 'all', intent: 'informational' });
  const [showAddQueryForm, setShowAddQueryForm]   = useState(false);
  const [runningTests, setRunningTests]           = useState(false);

  // ── Action feedback ──────────────────────────────────────────────────────────
  const [actionMsg, setActionMsg]       = useState('');
  const [actionMsgTimer, setActionMsgTimer] = useState(null);

  // ── Auth helpers ─────────────────────────────────────────────────────────────
  const authedFetch = useCallback(async (url, options = {}) => {
    if (!currentUser) throw new Error('Not authenticated.');
    const token = await currentUser.getIdToken();
    const base = String(process.env.REACT_APP_API_BASE_URL || '').trim();
    const resolvedUrl = (base && url.startsWith('/')) ? `${base}${url}` : url;
    return fetch(resolvedUrl, {
      ...options,
      headers: { Authorization: `Bearer ${token}`, ...(options.headers || {}) },
    });
  }, [currentUser]);

  const jsonFetch = useCallback(async (url, options = {}) => {
    const res = await authedFetch(url, {
      ...options,
      headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message || 'Request failed');
    return data;
  }, [authedFetch]);

  const flash = useCallback((msg, persistent = false) => {
    setActionMsg(msg);
    setActionMsgTimer((prev) => { if (prev) clearTimeout(prev); return null; });
    if (!persistent) {
      const t = setTimeout(() => setActionMsg(''), 6000);
      setActionMsgTimer(t);
    }
  }, []);

  // ── Data loaders ─────────────────────────────────────────────────────────────
  const loadDashboard = useCallback(async () => {
    setDashLoading(true);
    setDashError('');
    try {
      const data = await jsonFetch('/api/v1/blog/dashboard');
      setDashData(data.metrics);
    } catch (e) {
      setDashError(e.message);
    } finally {
      setDashLoading(false);
    }
  }, [jsonFetch]);

  const loadTopics = useCallback(async () => {
    setTopicsLoading(true);
    setTopicsError('');
    try {
      const qs = topicStatusFilter ? `?status=${topicStatusFilter}` : '';
      const data = await jsonFetch(`/api/v1/blog/topics${qs}`);
      setTopics(data.items || []);
    } catch (e) {
      setTopicsError(e.message);
    } finally {
      setTopicsLoading(false);
    }
  }, [jsonFetch, topicStatusFilter]);

  const loadPosts = useCallback(async () => {
    setPostsLoading(true);
    setPostsError('');
    try {
      const qs = postStatusFilter ? `?status=${postStatusFilter}` : '';
      const data = await jsonFetch(`/api/v1/blog/posts${qs}`);
      setPosts(data.items || []);
    } catch (e) {
      setPostsError(e.message);
    } finally {
      setPostsLoading(false);
    }
  }, [jsonFetch, postStatusFilter]);

  const loadPreviewPost = useCallback(async (id) => {
    setPreviewLoading(true);
    try {
      const data = await jsonFetch(`/api/v1/blog/posts/${id}`);
      setPreviewPost(data.post);
    } catch (e) {
      flash(`Failed to load post: ${e.message}`);
    } finally {
      setPreviewLoading(false);
    }
  }, [jsonFetch, flash]);

  const loadMonitor = useCallback(async () => {
    setMonitorLoading(true);
    try {
      const [summaryData, queriesData, resultsData] = await Promise.all([
        jsonFetch('/api/v1/ai-monitor/summary'),
        jsonFetch('/api/v1/ai-monitor/queries'),
        jsonFetch('/api/v1/ai-monitor/results?limit=200'),
      ]);
      setMonitorSummary(summaryData.data);
      setMonitorQueries((queriesData.data || []).filter(q => q.status !== 'deleted'));
      setMonitorResults(resultsData.data || []);
    } catch (e) {
      flash(`Failed to load monitor data: ${e.message}`);
    } finally {
      setMonitorLoading(false);
    }
  }, [jsonFetch, flash]);

  useEffect(() => { if (activeTab === 'dashboard') loadDashboard(); }, [activeTab, loadDashboard]);
  useEffect(() => { if (activeTab === 'topics') loadTopics(); }, [activeTab, loadTopics]);
  useEffect(() => { if (activeTab === 'posts') loadPosts(); }, [activeTab, loadPosts]);
  useEffect(() => { if (activeTab === 'monitor') loadMonitor(); }, [activeTab, loadMonitor]);

  useEffect(() => {
    if (previewPostId) {
      loadPreviewPost(previewPostId);
    } else {
      setPreviewPost(null);
    }
  }, [previewPostId, loadPreviewPost]);

  // ── Topic actions ─────────────────────────────────────────────────────────────
  const handleAddTopic = useCallback(async (e) => {
    e.preventDefault();
    setAddLoading(true);
    setAddError('');
    try {
      const keywords = addForm.target_keywords
        .split(',').map((k) => k.trim()).filter(Boolean);
      await jsonFetch('/api/v1/blog/topics', {
        method: 'POST',
        body: JSON.stringify({ ...addForm, target_keywords: keywords }),
      });
      setAddForm({ title: '', description: '', blog_category: 'design_trends', product_category: 'all', target_keywords: '', priority: 3, user_notes: '' });
      setShowAddForm(false);
      flash('Topic added.');
      loadTopics();
    } catch (e) {
      setAddError(e.message);
    } finally {
      setAddLoading(false);
    }
  }, [addForm, jsonFetch, flash, loadTopics]);

  const handleApproveTopic = useCallback(async (id) => {
    try {
      await jsonFetch(`/api/v1/blog/topics/${id}/approve`, { method: 'POST' });
      flash('Topic approved.');
      loadTopics();
    } catch (e) {
      flash(`Error: ${e.message}`);
    }
  }, [jsonFetch, flash, loadTopics]);

  const handleRejectTopic = useCallback(async (id) => {
    const reason = window.prompt('Rejection reason (optional):') || '';
    try {
      await jsonFetch(`/api/v1/blog/topics/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      flash('Topic rejected.');
      loadTopics();
    } catch (e) {
      flash(`Error: ${e.message}`);
    }
  }, [jsonFetch, flash, loadTopics]);

  const handleSuggestTopics = useCallback(async () => {
    setSuggestLoading(true);
    flash('Researching GSC data and generating topic suggestions… takes ~30 seconds.');
    try {
      const data = await jsonFetch('/api/v1/blog/suggest-topics', { method: 'POST', body: JSON.stringify({ count: 8 }) });
      flash(`✓ ${data.savedCount} new topics suggested from ${data.gscQueriesUsed} GSC queries.`);
      loadTopics();
    } catch (e) {
      flash(`Suggestion failed: ${e.message}`);
    } finally {
      setSuggestLoading(false);
    }
  }, [jsonFetch, flash, loadTopics]);

  const handleGenerateTopic = useCallback(async (id) => {
    setGeneratingTopicId(id);
    flash('Generating blog post… this may take 30–60 seconds.', true);
    try {
      const data = await jsonFetch('/api/v1/blog/generate', {
        method: 'POST',
        body: JSON.stringify({ topic_id: id }),
      });
      flash(`Blog generated successfully! Switch to Posts tab to review.`);
      loadTopics();
      loadPosts();
    } catch (e) {
      flash(`Generation failed: ${e.message}`);
    } finally {
      setGeneratingTopicId(null);
    }
  }, [jsonFetch, flash, loadTopics, loadPosts]);

  const handleSaveTopicTitle = useCallback(async (id) => {
    const newTitle = editingTopicTitle.trim();
    if (!newTitle) { flash('Title cannot be empty.'); return; }
    try {
      await jsonFetch(`/api/v1/blog/topics/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ title: newTitle }),
      });
      setEditingTopicId(null);
      setEditingTopicTitle('');
      flash('Topic title updated.');
      loadTopics();
    } catch (e) {
      flash(`Update failed: ${e.message}`);
    }
  }, [editingTopicTitle, jsonFetch, flash, loadTopics]);

  const handleSaveKeywords = useCallback(async (id) => {
    const keywords = editingKwValue.split(',').map(k => k.trim()).filter(Boolean);
    if (!keywords.length) { flash('Enter at least one keyword.'); return; }
    try {
      await jsonFetch(`/api/v1/blog/topics/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ target_keywords: keywords }),
      });
      setEditingKwTopicId(null);
      setEditingKwValue('');
      flash('Keywords updated.');
      loadTopics();
    } catch (e) {
      flash(`Update failed: ${e.message}`);
    }
  }, [editingKwValue, jsonFetch, flash, loadTopics]);

  const openCollectionModal = useCallback(async (topic) => {
    setCollectionModalTopic(topic);
    setCollectionSearch('');
    if (allCollections.length === 0) {
      try {
        const data = await jsonFetch('/api/v1/blog/collections');
        setAllCollections(data.data || []);
      } catch (e) {
        flash(`Could not load collections: ${e.message}`);
      }
    }
  }, [allCollections, jsonFetch, flash]);

  const handleSaveCollections = useCallback(async (topicId, updatedCollections) => {
    setSavingCollections(true);
    try {
      await jsonFetch(`/api/v1/blog/topics/${topicId}`, {
        method: 'PUT',
        body: JSON.stringify({ matched_collections: updatedCollections }),
      });
      flash('Collections saved.');
      setCollectionModalTopic((prev) => prev ? { ...prev, matched_collections: updatedCollections } : null);
      loadTopics();
    } catch (e) {
      flash(`Save failed: ${e.message}`);
    } finally {
      setSavingCollections(false);
    }
  }, [jsonFetch, flash, loadTopics]);

  // ── Post actions ──────────────────────────────────────────────────────────────
  const handleApprovePost = useCallback(async (id) => {
    try {
      await jsonFetch(`/api/v1/blog/posts/${id}/approve`, { method: 'POST' });
      flash('Post approved — added to publish queue. Scheduler will publish it at 11 AM IST.');
      loadPosts();
      loadDashboard();
      if (previewPostId === id) loadPreviewPost(id);
    } catch (e) {
      flash(`Approve failed: ${e.message}`);
    }
  }, [jsonFetch, flash, loadPosts, loadDashboard, previewPostId, loadPreviewPost]);

  const handlePublishNow = useCallback(async (id) => {
    flash('Publishing to Shopify…');
    try {
      const data = await jsonFetch(`/api/v1/blog/posts/${id}/publish`, { method: 'POST' });
      flash(data.shopify_url ? `Published! ${data.shopify_url}` : 'Published successfully.');
      loadPosts();
      loadDashboard();
      loadTopics();
      if (previewPostId === id) loadPreviewPost(id);
    } catch (e) {
      flash(`Publish failed: ${e.message}`);
    }
  }, [jsonFetch, flash, loadPosts, loadDashboard, loadTopics, previewPostId, loadPreviewPost]);

  const handleUnapprovePost = useCallback(async (id) => {
    try {
      await jsonFetch(`/api/v1/blog/posts/${id}/unapprove`, { method: 'POST' });
      flash('Post moved back to draft.');
      loadPosts();
      loadDashboard();
    } catch (e) {
      flash(`Failed: ${e.message}`);
    }
  }, [jsonFetch, flash, loadPosts, loadDashboard]);

  const handleRegeneratePost = useCallback(async (id) => {
    setRegeneratingPostId(id);
    flash('Regenerating post… this may take 30–60 seconds.', true);
    try {
      await jsonFetch(`/api/v1/blog/posts/${id}/regenerate`, { method: 'POST' });
      flash('Post regenerated successfully!');
      loadPosts();
      if (previewPostId === id) loadPreviewPost(id);
    } catch (e) {
      flash(`Regenerate failed: ${e.message}`);
    } finally {
      setRegeneratingPostId(null);
    }
  }, [jsonFetch, flash, loadPosts, previewPostId, loadPreviewPost]);

  const canCreate  = canAccessAction?.('blog.createTopic')  ?? true;
  const canApprove = canAccessAction?.('blog.approveTopic') ?? true;
  const canPublish = canAccessAction?.('blog.publishPost')  ?? true;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '24px', fontFamily: 'inherit' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 700 }}>Blog Automation</h1>
        {actionMsg && (
          <div style={{ padding: '8px 16px', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', color: '#166534', fontSize: '13px', maxWidth: '500px' }}>
            {actionMsg}
          </div>
        )}
      </div>

      {/* Tab navigation */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid #e5e7eb' }}>
        {['dashboard', 'topics', 'posts', 'monitor'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '8px 20px',
              border: 'none',
              borderBottom: activeTab === tab ? '2px solid #3b82f6' : '2px solid transparent',
              backgroundColor: 'transparent',
              color: activeTab === tab ? '#3b82f6' : '#6b7280',
              fontWeight: activeTab === tab ? 600 : 400,
              cursor: 'pointer',
              textTransform: 'capitalize',
              fontSize: '14px',
            }}
          >
            {tab === 'monitor' ? 'AI Monitor' : tab}
          </button>
        ))}
      </div>

      {/* ── Dashboard Tab ─────────────────────────────────────────────────────── */}
      {activeTab === 'dashboard' && (
        <div>
          {dashLoading && <p style={{ color: '#6b7280' }}>Loading…</p>}
          {dashError && <p style={{ color: '#ef4444' }}>{dashError}</p>}
          {dashData && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px', marginBottom: '32px' }}>
                {[
                  { label: 'Total Topics', value: dashData.topics?.total ?? 0 },
                  { label: 'Pending Approval', value: dashData.topics?.byStatus?.suggested ?? 0, color: '#f59e0b' },
                  { label: 'Posts Generated', value: dashData.posts?.total ?? 0 },
                  { label: 'In Publish Queue', value: dashData.posts?.queued ?? 0, color: '#6366f1' },
                  { label: 'Posts Published', value: dashData.posts?.byStatus?.published ?? 0, color: '#10b981' },
                  { label: 'Draft Posts', value: dashData.posts?.byStatus?.draft ?? 0, color: '#3b82f6' },
                  { label: 'Failed Topics', value: dashData.topics?.byStatus?.failed ?? 0, color: '#ef4444' },
                ].map((m) => (
                  <div key={m.label} style={{ padding: '16px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    <div style={{ fontSize: '26px', fontWeight: 700, color: m.color || '#111827' }}>{m.value}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{m.label}</div>
                  </div>
                ))}
              </div>

              <h3 style={{ marginBottom: '12px', fontSize: '15px', fontWeight: 600 }}>Recent Job Runs</h3>
              {dashData.recentLogs?.length === 0 && <p style={{ color: '#6b7280', fontSize: '13px' }}>No job runs yet.</p>}
              {dashData.recentLogs?.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f3f4f6' }}>
                      {['Run ID', 'Type', 'Status', 'Topics', 'Posts', 'Started', 'Duration'].map((h) => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dashData.recentLogs.map((log) => (
                      <tr key={log.id || log.run_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: '11px', color: '#6b7280' }}>{log.run_id}</td>
                        <td style={{ padding: '8px 12px' }}>{log.job_type}</td>
                        <td style={{ padding: '8px 12px' }}><StatusBadge status={log.status} /></td>
                        <td style={{ padding: '8px 12px' }}>{log.topics_processed ?? '—'}</td>
                        <td style={{ padding: '8px 12px' }}>{log.posts_generated ?? '—'}</td>
                        <td style={{ padding: '8px 12px' }}>{formatDate(log.started_at)}</td>
                        <td style={{ padding: '8px 12px' }}>{log.duration_ms ? `${(log.duration_ms / 1000).toFixed(1)}s` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Topics Tab ────────────────────────────────────────────────────────── */}
      {activeTab === 'topics' && (
        <div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
            <select
              value={topicStatusFilter}
              onChange={(e) => setTopicStatusFilter(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
            >
              <option value=''>All Statuses</option>
              {['suggested','approved','rejected','generating','generated','failed'].map((s) => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
            <button onClick={loadTopics} style={{ padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', backgroundColor: '#fff' }}>
              Refresh
            </button>
            {canApprove && (
              <button
                onClick={handleSuggestTopics}
                disabled={suggestLoading}
                style={{ padding: '6px 14px', backgroundColor: suggestLoading ? '#e5e7eb' : '#7c3aed', color: suggestLoading ? '#6b7280' : '#fff', border: 'none', borderRadius: '6px', cursor: suggestLoading ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600 }}
              >
                {suggestLoading ? 'Suggesting…' : '✦ Auto-Suggest Topics'}
              </button>
            )}
            {canCreate && (
              <button
                onClick={() => setShowAddForm((v) => !v)}
                style={{ padding: '6px 14px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
              >
                {showAddForm ? 'Cancel' : '+ Add Topic'}
              </button>
            )}
          </div>

          {/* Add Topic Form */}
          {showAddForm && (
            <form onSubmit={handleAddTopic} style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 600 }}>New Blog Topic</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Topic Title *</label>
                  <input
                    required value={addForm.title}
                    onChange={(e) => setAddForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder='e.g. Top Digital Print Fabric Trends for 2026'
                    style={inputStyle}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Description</label>
                  <textarea
                    value={addForm.description}
                    onChange={(e) => setAddForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder='Brief context for the blog writer…'
                    rows={2}
                    style={{ ...inputStyle, resize: 'vertical' }}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Blog Category</label>
                  <select value={addForm.blog_category} onChange={(e) => setAddForm((f) => ({ ...f, blog_category: e.target.value }))} style={inputStyle}>
                    {BLOG_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Product Category</label>
                  <select value={addForm.product_category} onChange={(e) => setAddForm((f) => ({ ...f, product_category: e.target.value }))} style={inputStyle}>
                    {PRODUCT_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Target Keywords (comma-separated)</label>
                  <input
                    value={addForm.target_keywords}
                    onChange={(e) => setAddForm((f) => ({ ...f, target_keywords: e.target.value }))}
                    placeholder='digital printed fabric, fabric trends 2026, cotton prints'
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Priority (1–5)</label>
                  <select value={addForm.priority} onChange={(e) => setAddForm((f) => ({ ...f, priority: parseInt(e.target.value, 10) }))} style={inputStyle}>
                    {[1,2,3,4,5].map((p) => <option key={p} value={p}>{p}{p === 3 ? ' (default)' : ''}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Notes for Writer</label>
                  <input
                    value={addForm.user_notes}
                    onChange={(e) => setAddForm((f) => ({ ...f, user_notes: e.target.value }))}
                    placeholder='Any specific angle or focus…'
                    style={inputStyle}
                  />
                </div>
              </div>
              {addError && <p style={{ color: '#ef4444', fontSize: '13px', margin: '12px 0 0' }}>{addError}</p>}
              <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
                <button type='submit' disabled={addLoading} style={{ padding: '8px 20px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>
                  {addLoading ? 'Adding…' : 'Add Topic'}
                </button>
                <button type='button' onClick={() => setShowAddForm(false)} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', backgroundColor: '#fff' }}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          {topicsLoading && <p style={{ color: '#6b7280' }}>Loading topics…</p>}
          {topicsError && <p style={{ color: '#ef4444' }}>{topicsError}</p>}
          {!topicsLoading && topics.length === 0 && <p style={{ color: '#6b7280', fontSize: '13px' }}>No topics found. Add one to get started.</p>}

          {topics.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6' }}>
                    {['Title', 'Category', 'Product', 'Keywords', 'Priority', 'Status', 'Created', 'Actions'].map((h) => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topics.map((t) => (
                    <tr key={t.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '10px 12px', maxWidth: '280px' }}>
                        {editingTopicId === t.id ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <input
                              value={editingTopicTitle}
                              onChange={(e) => setEditingTopicTitle(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTopicTitle(t.id); if (e.key === 'Escape') { setEditingTopicId(null); setEditingTopicTitle(''); } }}
                              autoFocus
                              style={{ fontSize: '13px', padding: '5px 8px', border: '1.5px solid #8b5cf6', borderRadius: '5px', width: '100%', outline: 'none' }}
                            />
                            <div style={{ display: 'flex', gap: '5px' }}>
                              <button onClick={() => handleSaveTopicTitle(t.id)} style={{ ...btnStyle('#10b981'), fontSize: '11px', padding: '3px 8px' }}>Save</button>
                              <button onClick={() => { setEditingTopicId(null); setEditingTopicTitle(''); }} style={{ ...btnStyle('#6b7280'), fontSize: '11px', padding: '3px 8px' }}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 500 }}>{t.title}</div>
                              {t.gsc_query && (
                                <div style={{ fontSize: '11px', color: '#6d28d9', marginTop: '2px', backgroundColor: '#ede9fe', padding: '1px 6px', borderRadius: '4px', display: 'inline-block' }}>
                                  GSC: "{t.gsc_query}"
                                </div>
                              )}
                              {t.user_notes && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{t.user_notes}</div>}
                              {/* Collection chip */}
                              <button
                                onClick={() => openCollectionModal(t)}
                                title="View / edit collections for this topic"
                                style={{ marginTop: '4px', display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px', padding: '2px 7px', borderRadius: '10px', border: '1px solid #d1d5db', background: (t.matched_collections?.length > 0) ? '#f0fdf4' : '#f9fafb', color: (t.matched_collections?.length > 0) ? '#15803d' : '#6b7280', cursor: 'pointer', fontWeight: 500 }}
                              >
                                🔗 {t.matched_collections?.length > 0 ? `${t.matched_collections.length} collections` : 'Add collections'}
                              </button>
                            </div>
                            <button
                              title="Edit title"
                              onClick={() => { setEditingTopicId(t.id); setEditingTopicTitle(t.title); }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '13px', padding: '2px', flexShrink: 0, lineHeight: 1 }}
                            >✏️</button>
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        {BLOG_CATEGORIES.find((c) => c.value === t.blog_category)?.label || t.blog_category}
                      </td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        {(() => {
                          const cycle = ['all', 'digital_printed', 'plain_dyed'];
                          const colors = { all: ['#6366f1', '#eef2ff'], digital_printed: ['#0891b2', '#ecfeff'], plain_dyed: ['#d97706', '#fffbeb'] };
                          const labels = { all: 'All', digital_printed: 'Digital', plain_dyed: 'Plain Dyed' };
                          const current = t.product_category || 'all';
                          const [textColor, bgColor] = colors[current] || colors.all;
                          const next = cycle[(cycle.indexOf(current) + 1) % cycle.length];
                          return (
                            <button
                              title={`Click to change → ${labels[next]}`}
                              onClick={async () => {
                                try {
                                  await jsonFetch(`/api/v1/blog/topics/${t.id}`, {
                                    method: 'PUT',
                                    body: JSON.stringify({ product_category: next }),
                                  });
                                  loadTopics();
                                } catch (e) { flash(`Update failed: ${e.message}`); }
                              }}
                              style={{ padding: '3px 10px', borderRadius: '12px', border: `1px solid ${textColor}55`, backgroundColor: bgColor, color: textColor, fontSize: '12px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                            >
                              {labels[current]} ↻
                            </button>
                          );
                        })()}
                      </td>
                      <td style={{ padding: '10px 12px', maxWidth: '200px' }}>
                        {editingKwTopicId === t.id ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <input
                              autoFocus
                              value={editingKwValue}
                              onChange={(e) => setEditingKwValue(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveKeywords(t.id); if (e.key === 'Escape') { setEditingKwTopicId(null); setEditingKwValue(''); } }}
                              placeholder="keyword1, keyword2, …"
                              style={{ fontSize: '12px', padding: '4px 6px', border: '1px solid #6366f1', borderRadius: '4px', width: '100%' }}
                            />
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button onClick={() => handleSaveKeywords(t.id)} style={{ ...btnStyle('#10b981'), fontSize: '11px', padding: '3px 8px' }}>Save</button>
                              <button onClick={() => { setEditingKwTopicId(null); setEditingKwValue(''); }} style={{ ...btnStyle('#6b7280'), fontSize: '11px', padding: '3px 8px' }}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                            <span style={{ color: '#6b7280', fontSize: '12px', flex: 1 }}>
                              {(t.target_keywords || []).slice(0, 3).join(', ')}{t.target_keywords?.length > 3 ? `… +${t.target_keywords.length - 3}` : ''}
                            </span>
                            <button
                              title="Edit keywords"
                              onClick={() => { setEditingKwTopicId(t.id); setEditingKwValue((t.target_keywords || []).join(', ')); }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', fontSize: '13px', color: '#9ca3af', flexShrink: 0 }}
                            >✏️</button>
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>{t.priority}</td>
                      <td style={{ padding: '10px 12px' }}><StatusBadge status={t.status} /></td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#6b7280' }}>{formatDate(t.createdAt)}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {canApprove && t.status === 'suggested' && (
                            <button onClick={() => handleApproveTopic(t.id)} style={btnStyle('#10b981')}>Approve</button>
                          )}
                          {canApprove && t.status === 'suggested' && (
                            <button onClick={() => handleRejectTopic(t.id)} style={btnStyle('#ef4444')}>Reject</button>
                          )}
                          {canCreate && ['approved', 'failed'].includes(t.status) && !t.generated_post_id && (
                            <button
                              onClick={() => handleGenerateTopic(t.id)}
                              disabled={generatingTopicId === t.id}
                              style={{ ...btnStyle('#8b5cf6'), opacity: generatingTopicId === t.id ? 0.6 : 1, cursor: generatingTopicId === t.id ? 'wait' : 'pointer' }}
                            >
                              {generatingTopicId === t.id ? '⏳ Generating…' : 'Generate'}
                            </button>
                          )}
                          {t.generated_post_id && (
                            <button
                              onClick={() => { setActiveTab('posts'); }}
                              style={{ ...btnStyle('#3b82f6'), fontSize: '11px' }}
                            >
                              View Post
                            </button>
                          )}
                          {t.generated_post_id && !t.shopify_published && canCreate && (
                            <button
                              onClick={async () => {
                                setRegeneratingPostId(t.generated_post_id);
                                flash('Regenerating blog post… this may take 30–60 seconds.', true);
                                try {
                                  await jsonFetch(`/api/v1/blog/posts/${t.generated_post_id}/regenerate`, { method: 'POST' });
                                  flash('Blog regenerated! Check Posts tab to review.');
                                  loadTopics();
                                  loadPosts();
                                } catch (e) {
                                  flash(`Regeneration failed: ${e.message}`);
                                } finally {
                                  setRegeneratingPostId(null);
                                }
                              }}
                              disabled={regeneratingPostId === t.generated_post_id}
                              style={{ ...btnStyle('#f59e0b'), opacity: regeneratingPostId === t.generated_post_id ? 0.6 : 1, cursor: regeneratingPostId === t.generated_post_id ? 'wait' : 'pointer', fontSize: '11px' }}
                            >
                              {regeneratingPostId === t.generated_post_id ? '⏳ Regenerating…' : '↺ Regenerate'}
                            </button>
                          )}
                          {t.last_generation_error && (
                            <span title={t.last_generation_error} style={{ fontSize: '11px', color: '#ef4444', cursor: 'help' }}>⚠ {t.generation_attempts}x failed</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Collection Picker Modal ───────────────────────────────────────────── */}
      {collectionModalTopic && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={(e) => { if (e.target === e.currentTarget) setCollectionModalTopic(null); }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', maxWidth: '560px', width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', gap: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '15px', color: '#111827' }}>Edit Collections</div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{collectionModalTopic.title}</div>
              </div>
              <button onClick={() => setCollectionModalTopic(null)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#9ca3af' }}>✕</button>
            </div>

            {/* Current collections */}
            <div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>SELECTED COLLECTIONS</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', minHeight: '36px', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#f9fafb' }}>
                {(collectionModalTopic.matched_collections || []).length === 0 && (
                  <span style={{ fontSize: '12px', color: '#9ca3af' }}>No collections selected — AI will auto-match on generation</span>
                )}
                {(collectionModalTopic.matched_collections || []).map((col) => (
                  <span key={col.handle} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', backgroundColor: '#dbeafe', color: '#1d4ed8', borderRadius: '12px', fontSize: '12px', fontWeight: 500 }}>
                    {col.title}
                    <button
                      onClick={() => {
                        const updated = (collectionModalTopic.matched_collections || []).filter((c) => c.handle !== col.handle);
                        setCollectionModalTopic((prev) => ({ ...prev, matched_collections: updated }));
                      }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', fontSize: '12px', padding: '0 0 0 2px', lineHeight: 1 }}
                    >✕</button>
                  </span>
                ))}
              </div>
            </div>

            {/* Search & add */}
            <div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>ADD FROM ALL COLLECTIONS</div>
              <input
                placeholder="Search collections…"
                value={collectionSearch}
                onChange={(e) => setCollectionSearch(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', marginBottom: '8px', boxSizing: 'border-box', outline: 'none' }}
              />
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                {allCollections
                  .filter((col) => col.title.toLowerCase().includes(collectionSearch.toLowerCase()) && !(collectionModalTopic.matched_collections || []).find((c) => c.handle === col.handle))
                  .map((col) => (
                    <div
                      key={col.handle}
                      onClick={() => {
                        const updated = [...(collectionModalTopic.matched_collections || []), { title: col.title, handle: col.handle, url: col.url }];
                        setCollectionModalTopic((prev) => ({ ...prev, matched_collections: updated }));
                        setCollectionSearch('');
                      }}
                      style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f3f4f6', color: '#374151' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f9ff'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                    >
                      {col.title}
                      <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: '6px' }}>{col.handle}</span>
                    </div>
                  ))}
                {allCollections.length === 0 && <div style={{ padding: '12px', fontSize: '12px', color: '#9ca3af' }}>Loading…</div>}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setCollectionModalTopic(null)} style={btnStyle('#6b7280')}>Cancel</button>
              <button
                onClick={() => handleSaveCollections(collectionModalTopic.id, collectionModalTopic.matched_collections || [])}
                disabled={savingCollections}
                style={{ ...btnStyle('#8b5cf6'), opacity: savingCollections ? 0.6 : 1 }}
              >
                {savingCollections ? 'Saving…' : 'Save Collections'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Posts Tab ─────────────────────────────────────────────────────────── */}
      {activeTab === 'posts' && (
        <div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
            <select
              value={postStatusFilter}
              onChange={(e) => setPostStatusFilter(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
            >
              <option value=''>All Statuses</option>
              {['draft','approved','publishing','published','publish_failed'].map((s) => (
                <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
              ))}
            </select>
            <button onClick={loadPosts} style={{ padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', backgroundColor: '#fff' }}>
              Refresh
            </button>
          </div>

          {postsLoading && <p style={{ color: '#6b7280' }}>Loading posts…</p>}
          {postsError && <p style={{ color: '#ef4444' }}>{postsError}</p>}
          {!postsLoading && posts.length === 0 && <p style={{ color: '#6b7280', fontSize: '13px' }}>No posts yet. Approve topics and generate them first.</p>}

          {posts.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6' }}>
                    {['SEO Title', 'Category', 'Words', 'Model', 'Status', 'Generated', 'Actions'].map((h) => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {posts.map((p) => (
                    <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: previewPostId === p.id ? '#eff6ff' : 'transparent' }}>
                      <td style={{ padding: '10px 12px', maxWidth: '240px' }}>
                        <div style={{ fontWeight: 500 }}>{p.seo_title}</div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{p.seo_description?.substring(0, 80)}{p.seo_description?.length > 80 ? '…' : ''}</div>
                      </td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        {BLOG_CATEGORIES.find((c) => c.value === p.blog_category)?.label || p.blog_category}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>{p.word_count ?? '—'}</td>
                      <td style={{ padding: '10px 12px', fontSize: '11px', color: '#6b7280' }}>{p.generation_model || '—'}</td>
                      <td style={{ padding: '10px 12px' }}><StatusBadge status={p.status} /></td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#6b7280' }}>{formatDate(p.createdAt)}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => setPreviewPostId(previewPostId === p.id ? null : p.id)}
                            style={btnStyle('#6b7280')}
                          >
                            {previewPostId === p.id ? 'Hide' : 'Preview'}
                          </button>
                          {/* draft / publish_failed → Approve (queue) + Regenerate */}
                          {canPublish && ['draft', 'publish_failed'].includes(p.status) && (
                            <button onClick={() => handleApprovePost(p.id)} style={btnStyle('#10b981')}>✓ Approve</button>
                          )}
                          {canCreate && ['draft', 'publish_failed'].includes(p.status) && (
                            <button
                              onClick={() => handleRegeneratePost(p.id)}
                              disabled={regeneratingPostId === p.id}
                              style={{ ...btnStyle('#8b5cf6'), opacity: regeneratingPostId === p.id ? 0.6 : 1, cursor: regeneratingPostId === p.id ? 'wait' : 'pointer' }}
                            >
                              {regeneratingPostId === p.id ? '⏳ Regenerating…' : 'Regenerate'}
                            </button>
                          )}
                          {/* approved (queued) → Publish Now + Reject back to draft */}
                          {canPublish && p.status === 'approved' && (
                            <>
                              <span style={{ fontSize: '11px', color: '#6366f1', fontWeight: 600, padding: '3px 7px', background: '#eef2ff', borderRadius: '4px', whiteSpace: 'nowrap' }}>⏳ In Queue</span>
                              <button onClick={() => handlePublishNow(p.id)} style={btnStyle('#10b981')}>🚀 Publish Now</button>
                              <button onClick={() => handleUnapprovePost(p.id)} style={btnStyle('#ef4444')}>✕ Reject</button>
                            </>
                          )}
                          {p.status === 'published' && p.published_url && (
                            <a href={p.published_url} target='_blank' rel='noreferrer' style={{ ...btnStyle('#10b981'), textDecoration: 'none', display: 'inline-block' }}>
                              View Live ↗
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Post Preview Panel */}
          {previewPostId && (
            <div style={{ marginTop: '24px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{ backgroundColor: '#f9fafb', padding: '12px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: '14px' }}>Post Preview</span>
                <button onClick={() => setPreviewPostId(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '18px' }}>×</button>
              </div>
              {previewLoading && <p style={{ padding: '20px', color: '#6b7280' }}>Loading…</p>}
              {previewPost && !previewLoading && (
                <div style={{ padding: '24px' }}>
                  {previewPost.hero_image_url ? (
                    <div style={{ marginBottom: '20px' }}>
                      <div style={metaLabelStyle}>Hero Image</div>
                      <img
                        src={previewPost.hero_image_url}
                        alt="Blog hero"
                        style={{ width: '100%', maxHeight: '280px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                      />
                    </div>
                  ) : (
                    <div style={{ marginBottom: '16px', padding: '10px 14px', backgroundColor: '#fef9c3', border: '1px solid #fde047', borderRadius: '6px', fontSize: '12px', color: '#854d0e' }}>
                      ⚠ No hero image generated — will publish without image
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                    {previewPost.title && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <div style={metaLabelStyle}>Display Title (H1)</div>
                        <div style={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>{previewPost.title}</div>
                      </div>
                    )}
                    <div>
                      <div style={metaLabelStyle}>SEO Title ({previewPost.seo_title?.length} chars)</div>
                      <div style={metaValueStyle}>{previewPost.seo_title}</div>
                    </div>
                    <div>
                      <div style={metaLabelStyle}>SEO Description ({previewPost.seo_description?.length} chars)</div>
                      <div style={metaValueStyle}>{previewPost.seo_description}</div>
                    </div>
                    <div>
                      <div style={metaLabelStyle}>Expert Summary</div>
                      <div style={metaValueStyle}>{previewPost.expert_summary}</div>
                    </div>
                    <div>
                      <div style={metaLabelStyle}>Tags</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {(previewPost.tags || []).map((tag) => (
                          <span key={tag} style={{ padding: '2px 8px', backgroundColor: '#dbeafe', color: '#1e40af', borderRadius: '12px', fontSize: '12px' }}>{tag}</span>
                        ))}
                      </div>
                    </div>
                    {previewPost.fabric_story?.heading && (
                      <div style={{ gridColumn: '1 / -1', backgroundColor: '#fdf4ff', border: '1px solid #e9d5ff', borderRadius: '6px', padding: '12px' }}>
                        <div style={metaLabelStyle}>Fabric Story</div>
                        <div style={{ fontWeight: 600, marginBottom: '6px' }}>{previewPost.fabric_story.heading}</div>
                        <div style={{ color: '#4b5563', fontSize: '13px', lineHeight: '1.6' }}>{previewPost.fabric_story.body}</div>
                      </div>
                    )}
                  </div>
                  <div style={metaLabelStyle}>Blog Content Preview</div>
                  <div
                    style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '20px', maxHeight: '500px', overflowY: 'auto', lineHeight: '1.7', fontSize: '14px' }}
                    dangerouslySetInnerHTML={{ __html: previewPost.main_content }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── AI Monitor Tab ────────────────────────────────────────────────────── */}
      {activeTab === 'monitor' && (
        <div>
          {monitorLoading && <p style={{ color: '#6b7280' }}>Loading monitor data…</p>}

          {/* Platform Status Cards */}
          {monitorSummary && (
            <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
              {[
                { name: 'Gemini', key: 'gemini', color: '#4285f4' },
                { name: 'ChatGPT', key: 'chatgpt', color: '#10b981' },
              ].map(({ name, key, color }) => {
                const configured = monitorSummary.platforms?.[key]?.configured;
                return (
                  <div key={key} style={{ padding: '12px 16px', border: '1px solid #e5e7eb', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px', minWidth: '160px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: configured ? '#10b981' : '#ef4444', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>{name}</div>
                      <div style={{ fontSize: '11px', color: configured ? '#10b981' : '#ef4444', marginTop: '2px' }}>
                        {configured ? 'Configured' : 'Add API Key'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Summary Cards */}
          {monitorSummary && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              {[
                {
                  label: 'Citation Rate',
                  value: monitorSummary.report?.summary?.citation_rate != null
                    ? `${monitorSummary.report.summary.citation_rate}%`
                    : '—',
                  color: '#3b82f6',
                },
                {
                  label: 'Queries Monitored',
                  value: monitorSummary.total_queries ?? 0,
                  color: '#111827',
                },
                {
                  label: 'Platforms Cited',
                  value: monitorSummary.report?.platform_breakdown
                    ? Object.values(monitorSummary.report.platform_breakdown).filter(p => p.cited > 0).length
                    : '—',
                  color: '#10b981',
                },
                {
                  label: 'Last Test Run',
                  value: monitorSummary.last_tested
                    ? formatDate(monitorSummary.last_tested)
                    : 'Never',
                  color: '#6b7280',
                  small: true,
                },
              ].map((m) => (
                <div key={m.label} style={{ padding: '16px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                  <div style={{ fontSize: m.small ? '14px' : '26px', fontWeight: 700, color: m.color }}>{m.value}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{m.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Action Row */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '24px' }}>
            <button
              onClick={async () => {
                if (runningTests) return;
                setRunningTests(true);
                try {
                  await jsonFetch('/api/v1/ai-monitor/run', { method: 'POST', body: JSON.stringify({}) });
                  flash('Tests started in background. Refresh in a few minutes to see results.', false);
                } catch (e) {
                  flash(`Run failed: ${e.message}`);
                } finally {
                  setRunningTests(false);
                }
              }}
              disabled={runningTests}
              style={{ padding: '8px 18px', backgroundColor: runningTests ? '#6b7280' : '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: runningTests ? 'wait' : 'pointer', fontSize: '13px', fontWeight: 600, opacity: runningTests ? 0.7 : 1 }}
            >
              {runningTests ? '⏳ Running…' : 'Run Tests Now'}
            </button>
            <button
              onClick={loadMonitor}
              style={{ padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', backgroundColor: '#fff' }}
            >
              Refresh
            </button>
          </div>

          {/* Queries Table */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>Monitored Queries</h3>
              <button
                onClick={() => setShowAddQueryForm(v => !v)}
                style={{ padding: '6px 14px', backgroundColor: '#3b82f611', color: '#3b82f6', border: '1px solid #3b82f644', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}
              >
                {showAddQueryForm ? 'Cancel' : '+ Add Query'}
              </button>
            </div>

            {/* Add Query Form */}
            {showAddQueryForm && (
              <div style={{ padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f9fafb', marginBottom: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '10px', alignItems: 'end' }}>
                  <div>
                    <label style={labelStyle}>Query</label>
                    <input
                      value={addQueryForm.query}
                      onChange={e => setAddQueryForm(f => ({ ...f, query: e.target.value }))}
                      placeholder="e.g. best fabric wholesale supplier India"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Category</label>
                    <select
                      value={addQueryForm.category}
                      onChange={e => setAddQueryForm(f => ({ ...f, category: e.target.value }))}
                      style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                    >
                      <option value="all">All Fabrics</option>
                      <option value="digital_printed">Digital Printed</option>
                      <option value="plain_dyed">Plain Dyed</option>
                      <option value="wholesale">Wholesale</option>
                      <option value="retail">Retail</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Intent</label>
                    <select
                      value={addQueryForm.intent}
                      onChange={e => setAddQueryForm(f => ({ ...f, intent: e.target.value }))}
                      style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                    >
                      <option value="informational">Informational</option>
                      <option value="commercial">Commercial</option>
                      <option value="navigational">Navigational</option>
                      <option value="transactional">Transactional</option>
                    </select>
                  </div>
                </div>
                <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={async () => {
                      if (!addQueryForm.query.trim()) return;
                      try {
                        await jsonFetch('/api/v1/ai-monitor/queries', {
                          method: 'POST',
                          body: JSON.stringify(addQueryForm),
                        });
                        flash('Query added.');
                        setAddQueryForm({ query: '', category: 'all', intent: 'informational' });
                        setShowAddQueryForm(false);
                        loadMonitor();
                      } catch (e) {
                        flash(`Error: ${e.message}`);
                      }
                    }}
                    style={{ padding: '7px 18px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                  >
                    Add
                  </button>
                </div>
              </div>
            )}

            {monitorQueries.length === 0 && !monitorLoading && (
              <p style={{ color: '#6b7280', fontSize: '13px' }}>No queries yet. Add one to start monitoring.</p>
            )}

            {monitorQueries.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f3f4f6' }}>
                      {['Query', 'Category', 'Last Tested', 'Citation Rate', 'Gemini', 'ChatGPT', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {monitorQueries.map(q => {
                      // Find latest result for this query
                      const latestResult = monitorResults
                        .filter(r => r.query_id === q.id)
                        .sort((a, b) => new Date(b.tested_at || 0) - new Date(a.tested_at || 0))[0];

                      const citationRate = q.last_citation_rate != null ? Math.round(q.last_citation_rate * 100) : null;
                      const citationColor = citationRate == null ? '#6b7280' : citationRate >= 60 ? '#10b981' : citationRate >= 30 ? '#f59e0b' : '#ef4444';

                      const platformIcon = (platform) => {
                        if (!latestResult) return '—';
                        const pr = latestResult.platform_results?.[platform];
                        if (!pr) return '—';
                        if (pr.skipped) return '⏸';
                        if (!pr.tested) return '—';
                        return pr.cited ? '✅' : '❌';
                      };

                      return (
                        <tr key={q.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '10px 12px', maxWidth: '280px' }}>
                            <div style={{ fontWeight: 500, color: '#111827' }}>{q.query}</div>
                          </td>
                          <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#6b7280', fontSize: '12px' }}>{q.category}</td>
                          <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#6b7280' }}>
                            {q.last_tested ? formatDate(q.last_tested) : 'Never'}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            {citationRate != null ? (
                              <span style={{
                                display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
                                backgroundColor: citationColor + '22', color: citationColor, border: `1px solid ${citationColor}44`,
                              }}>
                                {citationRate}%
                              </span>
                            ) : '—'}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: '16px' }}>{platformIcon('gemini')}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: '16px' }}>{platformIcon('chatgpt')}</td>
                          <td style={{ padding: '10px 12px' }}>
                            <button
                              onClick={async () => {
                                try {
                                  await jsonFetch(`/api/v1/ai-monitor/queries/${q.id}`, { method: 'DELETE' });
                                  flash('Query removed.');
                                  loadMonitor();
                                } catch (e) {
                                  flash(`Error: ${e.message}`);
                                }
                              }}
                              style={btnStyle('#ef4444')}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Opportunities Section */}
          {monitorQueries.length > 0 && (() => {
            const opportunities = monitorQueries.filter(q =>
              q.last_citation_rate === 0 || q.last_tested === null
            );
            if (!opportunities.length) return null;
            return (
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: 600 }}>
                  Opportunities — Queries With Zero Citations
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {opportunities.map(q => (
                    <div key={q.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', border: '1px solid #fde68a', borderRadius: '6px', backgroundColor: '#fffbeb' }}>
                      <span style={{ fontSize: '13px', color: '#92400e' }}>{q.query}</span>
                      <button
                        onClick={() => {
                          setAddForm(f => ({ ...f, title: q.query }));
                          setShowAddForm(true);
                          setActiveTab('topics');
                        }}
                        style={btnStyle('#f59e0b')}
                      >
                        Create Blog Topic
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Top Competitors Discovered */}
          {monitorSummary?.report?.top_competitors?.length > 0 && (
            <div>
              <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: 600 }}>Top Competitors Discovered</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', maxWidth: '480px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Brand Name</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Mentions</th>
                  </tr>
                </thead>
                <tbody>
                  {monitorSummary.report.top_competitors.map((c, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px 12px', color: '#111827' }}>{c.name}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center', color: '#6b7280' }}>{c.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const labelStyle = {
  display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px',
};
const inputStyle = {
  width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box',
};
const metaLabelStyle = {
  fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px',
};
const metaValueStyle = {
  fontSize: '13px', color: '#374151', lineHeight: '1.5',
};
function btnStyle(color) {
  return {
    padding: '4px 10px', backgroundColor: color + '11', color, border: `1px solid ${color}44`,
    borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap',
  };
}

export default BlogPage;
